import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { cx } from "@/utils/cx";
import { useThemeStore, type Theme } from "../../stores/themeStore";

/**
 * Theme toggle — segmented control s 3 možnostmi (Light/Dark/System).
 *
 * Renderován v Application Shell topbar vedle Avatar. Per REQ-UI-010 + UC-009
 * obecná dostupnost (Josef má větší kontrast preferenci) — viditelný button
 * group, žádný dropdown, aby šel ovládat 1 klikem na desktop i mobile.
 *
 * Touch target ≥ 48×48 px per REQ-UI-008/014/020.
 */
export function ThemeToggle({ className }: { className?: string }) {
  const { t } = useTranslation("common");
  const theme = useThemeStore((s) => s.theme);
  const setTheme = useThemeStore((s) => s.setTheme);

  const options: { value: Theme; label: string }[] = [
    { value: "light", label: t("theme.light") },
    { value: "dark", label: t("theme.dark") },
    { value: "system", label: t("theme.system") },
  ];

  return (
    <div
      className={cx(
        "inline-flex items-center gap-0.5 rounded-xl bg-secondary p-0.5 ring-1 ring-inset ring-secondary",
        className,
      )}
      role="radiogroup"
      aria-label={t("theme.label")}
    >
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          role="radio"
          aria-checked={theme === opt.value}
          data-active={theme === opt.value || undefined}
          className="min-h-touch min-w-touch rounded-lg border-none bg-transparent px-3 text-sm font-medium text-primary outline-focus-ring focus-visible:outline-2 focus-visible:outline-offset-2 data-active:bg-brand-solid data-active:text-white"
          onClick={() => setTheme(opt.value)}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

/**
 * Dropdown variant — voláno z Avatar menu (alternativa segmented).
 * Reuse uvnitř Application Shell Avatar dropdown.
 */
export function ThemeToggleMenu() {
  const { t } = useTranslation("common");
  const theme = useThemeStore((s) => s.theme);
  const setTheme = useThemeStore((s) => s.setTheme);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const currentLabel = theme === "light" ? t("theme.light") : theme === "dark" ? t("theme.dark") : t("theme.system");

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        className="inline-flex min-h-touch min-w-touch items-center gap-2 rounded-xl bg-primary px-3 text-sm text-primary ring-1 ring-inset ring-secondary"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((p) => !p)}
      >
        <span aria-hidden="true">⚙</span>
        <span className="sr-only">{t("theme.label")}: </span>
        <span>{currentLabel}</span>
      </button>
      {open && (
        <ul
          className="absolute top-[calc(100%+4px)] right-0 z-50 m-0 min-w-40 list-none rounded-xl bg-primary p-1 shadow-md ring-1 ring-inset ring-secondary"
          role="menu"
          aria-label={t("theme.label")}
        >
          {(["light", "dark", "system"] as Theme[]).map((opt) => (
            <li key={opt}>
              <button
                type="button"
                role="menuitemradio"
                aria-checked={theme === opt}
                className="min-h-touch w-full rounded-lg border-none bg-transparent px-3 text-left text-md text-primary aria-checked:bg-brand-solid aria-checked:font-semibold aria-checked:text-white"
                onClick={() => {
                  setTheme(opt);
                  setOpen(false);
                }}
              >
                {opt === "light" ? t("theme.light") : opt === "dark" ? t("theme.dark") : t("theme.system")}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
